import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { PlaceFeature } from '../../data/types';
import { BASE_LAYERS, layerById, shouldFallback, type BaseLayerId } from './baseLayers';
import { PLACE_TYPE_LABEL, PRECISION_LABEL, STATUS_LABEL } from '../../data/types';
import { useAppData } from '../../state/AppData';
import { useSelection } from '../../state/selection';

const SEGOVIA: L.LatLngExpression = [40.9505, -4.1235];

function icon(p: PlaceFeature, selected: boolean, sugerido = false): L.DivIcon {
  const pr = p.properties;
  const approx = pr.geometryPrecision !== 'exact' && pr.geometryPrecision !== 'building';
  return L.divIcon({
    className: 'pin-wrap',
    html: `<span class="pin pin--${pr.type} pin--st-${pr.currentStatus}${selected ? ' is-selected' : ''}${approx ? ' is-approx' : ''}${sugerido ? ' is-sugerido' : ''}"><span class="pin__dot"></span><span class="pin__label">${escapeHtml(pr.canonicalName)}</span></span>`,
    iconSize: [22, 22],
    iconAnchor: [11, 11],
  });
}

/** El lugar del que más habla el libro: el que se propone cuando no hay nada elegido. */
function masMencionado(places: PlaceFeature[], mentionsByPlace: Map<string, unknown[]>): string | null {
  let mejor: string | null = null;
  let max = 0;
  for (const p of places) {
    const n = mentionsByPlace.get(p.id)?.length ?? 0;
    if (n > max) { max = n; mejor = p.id; }
  }
  return mejor;
}

const LABEL_ZOOM = 17;
const markerLabel = (p: PlaceFeature) => {
  const pr = p.properties;
  const approx = pr.geometryPrecision === 'exact' || pr.geometryPrecision === 'building'
    ? '' : ` Ubicación: ${PRECISION_LABEL[pr.geometryPrecision]?.toLowerCase() ?? pr.geometryPrecision}.`;
  return `${pr.canonicalName} (${PLACE_TYPE_LABEL[pr.type] ?? pr.type}; ${STATUS_LABEL[pr.currentStatus] ?? pr.currentStatus}). ${pr.mentionCount} ${pr.mentionCount === 1 ? 'mención' : 'menciones'}.${approx}`;
};

const escapeHtml = (s: string) => s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));

export function PlaceMap({ places, route = false }: { places: PlaceFeature[]; route?: boolean }) {
  const el = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const markers = useRef(new Map<string, L.Marker>());
  const [tileError, setTileError] = useState(false);
  const [fellBack, setFellBack] = useState<BaseLayerId | null>(null);
  const [params, setParams] = useSearchParams();
  const requested = layerById(params.get('capa'));
  const [active, setActive] = useState<BaseLayerId>(requested.id);
  const baseRef = useRef<L.LayerGroup | null>(null);
  // si la URL cambia (enlace, atrás/adelante), la capa la sigue
  const urlLayer = requested.id;
  useEffect(() => { setFellBack(null); setActive(urlLayer); }, [urlLayer]);
  const sel = useSelection();
  const selectRef = useRef(sel.selectPlace);
  selectRef.current = sel.selectPlace;
  // los marcadores se pintan en un efecto que solo depende de los lugares: la selección se lee por
  // referencia para no volver a crearlos todos cada vez que cambia
  const selRef = useRef(sel);
  selRef.current = sel;
  const sugeridoRef = useRef<string | null>(null);
  const { mentionsByPlace } = useAppData();

  // creación única del mapa
  useEffect(() => {
    if (!el.current) return;
    const map = L.map(el.current, { center: SEGOVIA, zoom: 15, maxZoom: 20, zoomControl: true, keyboard: true });
    map.attributionControl.setPrefix('<a href="https://leafletjs.com">Leaflet</a>');
    mapRef.current = map;
    // con muchos lugares, los rótulos solo se muestran de cerca (o al pasar el ratón / seleccionar)
    const syncLabels = () => el.current?.classList.toggle('show-labels', map.getZoom() >= LABEL_ZOOM);
    map.on('zoomend', syncLabels);
    syncLabels();
    const ro = new ResizeObserver(() => map.invalidateSize());
    ro.observe(el.current);
    return () => { ro.disconnect(); map.remove(); mapRef.current = null; markers.current.clear(); };
  }, []);

  // mapa base: IGN por defecto; si no responde, respaldo automático a OSM (ADR-0004)
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    baseRef.current?.remove();
    const def = layerById(active);
    const counts = { errors: 0, loads: 0 };
    const tiles = L.tileLayer(def.url, { maxZoom: 20, maxNativeZoom: def.maxNativeZoom, attribution: def.attribution, crossOrigin: true });
    tiles.on('tileload', () => { counts.loads++; setTileError(false); });
    tiles.on('tileerror', () => {
      counts.errors++;
      if (!shouldFallback(counts)) return;
      if (def.id !== 'osm') { setFellBack(def.id); setActive('osm'); }
      else setTileError(true);
    });
    const group = L.layerGroup([tiles]);
    if (def.overlay) group.addLayer(L.tileLayer(def.overlay.url, { maxZoom: 20, maxNativeZoom: def.maxNativeZoom, crossOrigin: true }));
    group.addTo(map);
    baseRef.current = group;
    return () => { group.remove(); };
  }, [active]);

  const chooseLayer = (id: BaseLayerId) => {
    setFellBack(null);
    setTileError(false);
    setActive(id);
    setParams((p) => { const n = new URLSearchParams(p); if (id === BASE_LAYERS[0].id) n.delete('capa'); else n.set('capa', id); return n; }, { replace: true });
  };

  // trazado del itinerario: une en orden las paradas con ubicación (línea recta, no ruta a pie)
  const routeRef = useRef<L.Polyline | null>(null);
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    routeRef.current?.remove();
    routeRef.current = null;
    if (!route) return;
    const pts = places.filter((p) => p.geometry).map((p) => [p.geometry!.coordinates[1], p.geometry!.coordinates[0]] as [number, number]);
    if (pts.length < 2) return;
    routeRef.current = L.polyline(pts, { color: '#1d3557', weight: 3, opacity: .65, dashArray: '6 6' }).addTo(map);
    return () => { routeRef.current?.remove(); routeRef.current = null; };
  }, [places, route]);

  // marcadores (solo lugares con geometría; el resto aparece en la lista)
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    markers.current.forEach((m) => m.remove());
    markers.current.clear();
    const located = places.filter((p) => p.geometry);
    // el lugar más presente en el libro late al abrir: da un punto por donde empezar entre decenas
    // de marcadores iguales. Si ya hay algo elegido, no hay nada que sugerir.
    sugeridoRef.current = selRef.current.place ? null : masMencionado(located, mentionsByPlace);
    const sugerido = sugeridoRef.current;
    for (const p of located) {
      const [lon, lat] = p.geometry!.coordinates;
      const m = L.marker([lat, lon], { icon: icon(p, false, p.id === sugerido), keyboard: true, title: p.properties.canonicalName, riseOnHover: true })
        .on('click', () => selectRef.current(p.id, 'map')) // Leaflet emite 'click' también con Intro
        .addTo(map);
      const node = m.getElement();
      node?.setAttribute('role', 'button');
      node?.setAttribute('aria-label', markerLabel(p));
      node?.setAttribute('data-place', p.id);
      markers.current.set(p.id, m);
    }
    if (located.length > 1) {
      map.fitBounds(L.latLngBounds(located.map((p) => [p.geometry!.coordinates[1], p.geometry!.coordinates[0]] as [number, number])), { padding: [40, 40], maxZoom: 16 });
    }
  }, [places]);

  // selección → resaltar y, si hace falta, encuadrar (nunca cuando el cambio viene del propio mapa)
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    for (const [id, m] of markers.current) {
      const p = places.find((x) => x.id === id)!;
      const selected = id === sel.place;
      // el latido solo mientras no se ha elegido nada: en cuanto hay selección deja de proponer
      m.setIcon(icon(p, selected, !sel.place && id === sugeridoRef.current));
      m.setZIndexOffset(selected ? 1000 : 0);
      const node = m.getElement();
      node?.setAttribute('role', 'button');
      node?.setAttribute('aria-pressed', String(selected));
      node?.setAttribute('data-place', id);
      node?.setAttribute('aria-label', markerLabel(p));
    }
    const m = sel.place ? markers.current.get(sel.place) : undefined;
    if (m && sel.origin !== 'map') {
      const inner = map.getBounds().pad(-0.15);
      if (!inner.contains(m.getLatLng())) {
        const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
        map.panTo(m.getLatLng(), { animate: !reduce });
      }
    }
  }, [sel.place, sel.origin, places]);

  const selected = sel.place ? places.find((p) => p.id === sel.place) : undefined;
  return (
    <div className="map">
      <div ref={el} className="map__canvas" role="region" aria-label="Mapa de lugares. La lista de lugares ofrece la misma información sin mapa." />
      {!selected && (
        // el mapa parecía la ilustración de un dato, no algo que se toca: mientras no haya nada
        // elegido, lo dice con todas las letras y desaparece en cuanto se usa
        <p className="map__cue">Pulsa un punto y abre sus pasajes del libro</p>
      )}
      <div className="map__layers segmented" role="group" aria-label="Mapa base">
        {BASE_LAYERS.map((l) => (
          <button key={l.id} type="button" aria-pressed={active === l.id} onClick={() => chooseLayer(l.id)}>{l.label}</button>
        ))}
      </div>
      {fellBack && !tileError && (
        <p className="map__notice map__notice--warn" role="status">
          El {fellBack === 'pnoa' ? 'servicio de ortofoto' : 'mapa'} del IGN no responde; se muestra OpenStreetMap.
        </p>
      )}
      {tileError && <p className="map__notice" role="status">No se puede cargar el mapa base. Los lugares siguen disponibles en la lista.</p>}
      {selected && !selected.geometry && (
        <p className="map__notice map__notice--info" role="status">
          <strong>{selected.properties.canonicalName}</strong> no tiene localización publicada: aparece en el texto y en la lista, pero no en el mapa.
        </p>
      )}
      <div className="map__legend" aria-hidden="true">
        <span><i className="pin__dot pin__dot--legend" /> Se conserva (igual o transformado)</span>
        <span><i className="pin__dot pin__dot--legend pin__dot--lost" /> Desaparecido o en ruina</span>
        <span><i className="pin__dot pin__dot--legend pin__dot--approx" /> Ubicación aproximada o de término</span>
      </div>
    </div>
  );
}
